// RC-5 manual attendance correction — raw scans are never touched; the day is
// replaced with source=manual + reason + actor, and presence recomputes.
//
// ⚠️ P3: an approved or payroll-seen day is NEVER rewritten — the server turns
// the correction into a reconciliation notice, and this says so.
// ⚠️ QA A16: pre-fills the day's ACTUAL first/last (as it now stands), or
// leaves the field blank — never an invented 08:00 / 17:00.
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Loader2 } from 'lucide-react';
import { Button } from '@/features/shared/components/ui/button';
import { Input } from '@/features/shared/components/ui/input';
import { Label } from '@/features/shared/components/ui/label';
import { Textarea } from '@/features/shared/components/ui/textarea';
import { getApiErrorMessage } from '@/lib/apiError';
import { attendanceDayDetail } from '@/services/attendanceService';
import { correctAttendanceDay } from '@/services/workAssignmentService';

export type CorrectionTarget = { employeeId: string; workDate: string };

export const CorrectionDialog: React.FC<{
  ex: CorrectionTarget; nameOf: (id?: string | null) => string;
  onClose: () => void; onDone: () => void;
}> = ({ ex, nameOf, onClose, onDone }) => {
  const { t } = useTranslation('admin');
  const [clockIn, setClockIn] = useState('');
  const [clockOut, setClockOut] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    attendanceDayDetail(ex.employeeId, ex.workDate)
      .then((d) => {
        if (!alive) return;
        setClockIn((v) => v || d?.breakdown?.first_in || '');
        setClockOut((v) => v || d?.breakdown?.last_out || '');
      })
      .catch(() => undefined); // nothing recorded: stay blank
    return () => { alive = false; };
  }, [ex.employeeId, ex.workDate]);

  const submit = async () => {
    setBusy(true);
    try {
      const res = await correctAttendanceDay({
        employee_id: ex.employeeId, work_date: ex.workDate,
        clock_in: clockIn, clock_out: clockOut, reason,
      });
      if (res && res.applied === false) {
        toast(t('wa.correction_notice', {
          defaultValue: 'Not applied — {{why}}',
          why: res.reason || t('wa.correction_locked', { defaultValue: 'the day is approved or already in payroll. A reconciliation notice was raised.' }),
        }), { icon: '⚠️', duration: 7000 });
      } else {
        toast.success(t('wa.corrected', { defaultValue: 'Attendance corrected — presence will recompute.' }));
      }
      onDone();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not correct attendance.'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/20 p-4" onClick={onClose}>
      <div className="w-full max-w-sm rounded-lg bg-background border border-border p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-h4 text-charcoal">{t('wa.correct_title', { defaultValue: 'Correct attendance' })}</h3>
        <p className="text-sm text-muted-foreground">{nameOf(ex.employeeId)} · {ex.workDate}</p>
        <div className="grid grid-cols-2 gap-3">
          <div><Label htmlFor="corr-in">{t('wa.clock_in', { defaultValue: 'Clock in' })}</Label>
            <Input id="corr-in" type="time" value={clockIn} onChange={(e) => setClockIn(e.target.value)} /></div>
          <div><Label htmlFor="corr-out">{t('wa.clock_out', { defaultValue: 'Clock out' })}</Label>
            <Input id="corr-out" type="time" value={clockOut} onChange={(e) => setClockOut(e.target.value)} /></div>
        </div>
        <div>
          <Label>{t('wa.reason', { defaultValue: 'Reason (required, audited)' })}</Label>
          <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder={t('wa.reason_ph', { defaultValue: 'Scanner was offline; employee worked full day on site.' })} />
          <p className={`text-xs ${reason.trim().length < 5 ? 'text-warning' : 'text-muted-foreground'}`}>
            {t('wa.reason_min', { defaultValue: 'At least {{n}} characters', n: 5 }).replace('{{n}}', '5')} ({reason.trim().length}/5)
          </p>
        </div>
        <p className="text-xs text-muted-foreground">
          {t('wa.correct_note', { defaultValue: 'Original scans are preserved; the day is recorded as a manual correction with your name.' })}
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>{t('common.cancel', { defaultValue: 'Cancel' })}</Button>
          <Button onClick={submit} disabled={busy || reason.trim().length < 5 || !clockIn || !clockOut}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t('wa.correct_submit', { defaultValue: 'Correct day' })}
          </Button>
        </div>
      </div>
    </div>
  );
};
